import React, { useState, useEffect } from 'react';
import { BlogManager } from '../data/blogData';
import { useAllBlogViewCounts } from '../hooks/useBlogAnalytics';
import useAnalytics from '../hooks/useAnalytics';
import { ShareIcon, ArrowLeftIcon, ClockIcon, EyeIcon } from './Icons';
import NotFound from './NotFound';

const BlogDetail = ({ slug, onClose }) => {
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startTime, setStartTime] = useState(null);
    const { trackBlogRead, trackPageView, trackBlogEngagement } = useAnalytics();

    // Use the same approach as homepage for consistency
    const { viewCounts, loading: viewCountsLoading } = useAllBlogViewCounts();

    // Calculate view count using same logic as homepage blog cards
    const viewCount = blog && viewCounts[blog.slug] !== undefined
        ? viewCounts[blog.slug]
        : blog?.views || 0;
    const isRealData = blog && viewCounts[blog.slug] !== undefined;

    useEffect(() => {
        const fetchBlog = () => {
            setLoading(true);
            // Simulate API call delay
            setTimeout(() => {
                const blogPost = BlogManager.getPostBySlug(slug);
                setBlog(blogPost);
                setLoading(false);

                // Track blog view event when blog loads successfully
                if (blogPost) {
                    trackBlogRead(blogPost.id, blogPost.title, slug);
                    trackPageView(`blog_detail_${blogPost.id}`, {
                        blog_id: blogPost.id,
                        blog_title: blogPost.title,
                        blog_slug: slug,
                        blog_category: blogPost.category
                    });
                    setStartTime(Date.now());
                }
            }, 100);
        };

        if (slug) {
            fetchBlog();
        }
    }, [slug]); // Removed trackBlogRead and trackPageView from dependencies

    // Track reading time when component unmounts or user navigates away
    useEffect(() => {
        return () => {
            if (startTime && blog) {
                const readingTime = Math.round((Date.now() - startTime) / 1000); // in seconds
                if (readingTime > 5) { // Only track if they spent more than 5 seconds
                    trackBlogEngagement(blog.id, slug, 'reading_time', {
                        reading_time_seconds: readingTime,
                        blog_title: blog.title
                    });
                }
            }
        };
    }, [startTime, blog, slug]); // Removed trackBlogEngagement from dependencies


    const handleShare = async () => {
        if (navigator.share && blog) {
            try {
                await navigator.share({
                    title: blog.title,
                    text: blog.excerpt,
                    url: window.location.href
                });
                trackBlogEngagement(blog.id, slug, 'shared', {
                    share_method: 'native_share',
                    blog_title: blog.title
                });
            } catch (err) {
                // Fallback to clipboard
                navigator.clipboard.writeText(window.location.href);
                trackBlogEngagement(blog.id, slug, 'shared', {
                    share_method: 'clipboard',
                    blog_title: blog.title
                });
            }
        } else {
            // Fallback to clipboard
            navigator.clipboard.writeText(window.location.href);
            trackBlogEngagement(blog.id, slug, 'shared', {
                share_method: 'clipboard',
                blog_title: blog.title
            });
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-cream-100 z-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-500"></div>
            </div>
        );
    }

    if (!blog) {
        return <NotFound type="blog" />;
    }

    const category = BlogManager.getCategoryById(blog.category);

    return (
        <div className="fixed inset-0 bg-cream-100 z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-cream-100/95 backdrop-blur-md border-b border-border-light z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center space-x-2 text-charcoal-600 hover:text-charcoal-900 transition-colors"
                    >
                        <ArrowLeftIcon className="w-5 h-5" />
                        <span className="hidden sm:inline">Back to Home</span>
                        <span className="sm:hidden">Back</span>
                    </button>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleShare}
                            className="flex items-center space-x-2 px-3 py-2 rounded-full text-charcoal-600 hover:text-charcoal-900 hover:bg-cream-200 transition-colors"
                        >
                            <ShareIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">Share</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {/* Article Header */}
                <header className="mb-8 sm:mb-12">
                    {/* Category Badge */}
                    {category && (
                        <div className="mb-4">
                            <span className="inline-block px-3 py-1 text-sm font-medium text-white rounded-full bg-coral-500">
                                {category.name}
                            </span>
                        </div>
                    )}

                    {/* Title */}
                    <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-charcoal-900 mb-6 leading-tight">
                        {blog.title}
                    </h1>

                    {/* Excerpt */}
                    <p className="text-lg sm:text-xl text-charcoal-600 mb-6 leading-relaxed">
                        {blog.excerpt}
                    </p>

                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-charcoal-500 mb-8 pb-8 border-b border-border-light">
                        <div className="flex items-center space-x-2">
                            <img
                                src="/images/profile/image.webp"
                                alt={blog.author}
                                className="w-8 h-8 object-cover rounded-lg"
                            />
                            <span className="font-medium text-charcoal-700">{blog.author}</span>
                        </div>

                        <div className="flex items-center space-x-1.5">
                            <ClockIcon className="w-4 h-4" />
                            <span>{blog.readTime}</span>
                        </div>

                        <div className="flex items-center space-x-1.5">
                            <EyeIcon className="w-4 h-4" />
                            <span>
                                {viewCountsLoading ? (
                                    <span className="animate-pulse">Loading...</span>
                                ) : (
                                    <>
                                        {(viewCount || 0).toLocaleString()} views
                                        {isRealData && <span className="text-coral-500 ml-1 text-xs">•</span>}
                                    </>
                                )}
                            </span>
                        </div>

                        <time dateTime={blog.publishedAt} className="text-charcoal-500">
                            {new Date(blog.publishedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </time>
                    </div>

                    {/* Featured Image */}
                    {blog.image && (
                        <div className="mb-8 sm:mb-12 rounded-2xl overflow-hidden border border-border-light">
                            <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-full h-64 md:h-96 object-cover"
                            />
                        </div>
                    )}

                    {/* Tags */}
                    {blog.tags && blog.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-8">
                            {blog.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    className="px-3 py-1 bg-cream-200 text-charcoal-700 text-sm rounded-full hover:bg-coral-50 hover:text-coral-600 transition-colors"
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}
                </header>

                {/* Article Content */}
                <div
                    className="prose prose-lg max-w-none
                        prose-headings:font-serif prose-headings:text-charcoal-900 prose-headings:font-medium
                        prose-p:text-charcoal-800 prose-p:leading-relaxed
                        prose-a:text-coral-600 prose-a:no-underline hover:prose-a:text-coral-700
                        prose-strong:text-charcoal-900
                        prose-code:text-coral-600 prose-code:bg-cream-200 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                        prose-pre:bg-charcoal-900 prose-pre:text-cream-100
                        prose-blockquote:border-l-coral-500 prose-blockquote:text-charcoal-700 prose-blockquote:italic
                        prose-li:text-charcoal-800
                        prose-img:rounded-xl prose-img:border prose-img:border-border-light
                        [&>p]:text-charcoal-800 [&>ul]:text-charcoal-800 [&>ol]:text-charcoal-800"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                />

                {/* Article Footer */}
                <footer className="mt-12 sm:mt-16 pt-8 border-t border-border-light">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center space-x-2 text-charcoal-500">
                            <EyeIcon className="w-5 h-5" />
                            <span>
                                {viewCountsLoading ? (
                                    <span className="animate-pulse">Loading...</span>
                                ) : (
                                    <>
                                        {(viewCount || 0).toLocaleString()} views
                                        {isRealData && <span className="text-coral-500 ml-1 text-xs">• Live</span>}
                                    </>
                                )}
                            </span>
                        </div>

                        <button
                            onClick={handleShare}
                            className="flex items-center space-x-2 px-4 py-2 rounded-full border border-border-light text-charcoal-600 hover:text-charcoal-900 hover:border-charcoal-900 hover:bg-charcoal-900 hover:text-white transition-all"
                        >
                            <ShareIcon className="w-5 h-5" />
                            <span>Share Article</span>
                        </button>
                    </div>
                </footer>
            </article>
        </div>
    );
};

export default BlogDetail;
